/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

// =============================================
// 10 MINUTE CART RESERVATION TIMER
// SYMMETRY 7.0.1
// =============================================

// =============================================
// CART RESERVATION TIMER
// Symmetry 7.0.1
// 10 MINUTES
// =============================================

(function () {
  'use strict';

  const TIMER_DURATION = 10 * 60 * 1000;
  const STORAGE_KEY = 'cartReservationExpiresAt';

  let timerInterval = null;

  // ---------------------------------------------
  // Get timer elements
  // ---------------------------------------------
  function getElements() {
    return {
      wrapper: document.getElementById('cart-reservation-timer'),
      countdown: document.getElementById('cart-countdown')
    };
  }

  // ---------------------------------------------
  // Format timer
  // Example: 09 min 58 sec
  // ---------------------------------------------
  function formatTime(ms) {
    const totalSeconds = Math.max(0, Math.ceil(ms / 1000));

    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return (
      String(minutes).padStart(2, '0') +
      ' min ' +
      String(seconds).padStart(2, '0') +
      ' sec'
    );
  }

  // ---------------------------------------------
  // Show timer
  // ---------------------------------------------
  function showTimer() {
    const elements = getElements();

    if (!elements.wrapper) {
      return;
    }

    elements.wrapper.style.display = 'block';
  }

  // ---------------------------------------------
  // Hide timer
  // ---------------------------------------------
  function hideTimer() {
    const elements = getElements();

    if (!elements.wrapper) {
      return;
    }

    elements.wrapper.style.display = 'none';
  }

  // ---------------------------------------------
  // Update timer
  // ---------------------------------------------
  function updateTimer() {
    const elements = getElements();

    if (!elements.wrapper || !elements.countdown) {
      return;
    }

    const expiresAt = parseInt(
      localStorage.getItem(STORAGE_KEY),
      10
    );

    // No timer exists
    if (!expiresAt) {
      hideTimer();
      return;
    }

    const remaining = expiresAt - Date.now();

    // Timer expired
    if (remaining <= 0) {
      elements.countdown.textContent = '00 min 00 sec';

      showTimer();

      if (timerInterval) {
        clearInterval(timerInterval);
        timerInterval = null;
      }

      return;
    }

    elements.countdown.textContent = formatTime(remaining);

    showTimer();
  }

  // ---------------------------------------------
  // Start timer
  // ---------------------------------------------
  function startTimer() {
    let expiresAt = parseInt(
      localStorage.getItem(STORAGE_KEY),
      10
    );

    // Create new 10 minute timer
    if (!expiresAt || expiresAt <= Date.now()) {
      expiresAt = Date.now() + TIMER_DURATION;

      localStorage.setItem(
        STORAGE_KEY,
        String(expiresAt)
      );
    }

    // Update immediately
    updateTimer();

    // Clear previous interval
    if (timerInterval) {
      clearInterval(timerInterval);
    }

    // Start countdown
    timerInterval = setInterval(function () {
      updateTimer();
    }, 1000);
  }

  // ---------------------------------------------
  // Clear timer
  // ---------------------------------------------
  function clearTimer() {
    localStorage.removeItem(STORAGE_KEY);

    if (timerInterval) {
      clearInterval(timerInterval);
      timerInterval = null;
    }

    hideTimer();
  }

  // ---------------------------------------------
  // Check Shopify cart
  // ---------------------------------------------
  async function checkCart() {
    try {
      const response = await fetch(
        window.Shopify.routes.root + 'cart.js',
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Cart request failed');
      }

      const cart = await response.json();

      if (cart.item_count > 0) {
        startTimer();
      } else {
        clearTimer();
      }

    } catch (error) {
      console.error(
        'Cart reservation timer:',
        error
      );
    }
  }

  // ---------------------------------------------
  // Product added to cart
  // ---------------------------------------------
  document.addEventListener(
    'on:cart:add',
    function () {

      // Start immediately
      startTimer();

      // Give Symmetry cart drawer time to render/update
      setTimeout(function () {
        checkCart();
      }, 100);

      // Extra fallback
      setTimeout(function () {
        updateTimer();
      }, 500);
    }
  );

  // ---------------------------------------------
  // Cart updated / merged
  // ---------------------------------------------
  document.addEventListener(
    'on:cart:after-merge',
    function () {
      checkCart();
    }
  );

  // ---------------------------------------------
  // Cart drawer opened
  // ---------------------------------------------
  document.addEventListener(
    'on:cart-drawer:after-open',
    function () {

      checkCart();

      setTimeout(function () {
        updateTimer();
      }, 100);

      setTimeout(function () {
        updateTimer();
      }, 500);
    }
  );

  // ---------------------------------------------
  // Listen for Shopify cart changes
  // ---------------------------------------------
  document.addEventListener(
    'cart:updated',
    function () {
      checkCart();
    }
  );

  // ---------------------------------------------
  // Initial page load
  // ---------------------------------------------
  function init() {
    checkCart();

    // If timer already exists in localStorage,
    // keep it running after page load.
    const expiresAt = parseInt(
      localStorage.getItem(STORAGE_KEY),
      10
    );

    if (expiresAt && expiresAt > Date.now()) {
      startTimer();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      init
    );
  } else {
    init();
  }

  // ---------------------------------------------
  // Expose functions globally
  // ---------------------------------------------
  window.startCartReservationTimer = startTimer;
  window.clearCartReservationTimer = clearTimer;
  window.updateCartReservationTimer = updateTimer;

})();
